import type { Capability } from '@clawharbor/manifest';
import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import type { RunResult } from '../api.js';
import type { DashboardActions, RunState } from '../store.js';
import { ErrorNote, Scroller, fmtMs } from './bits.js';
import { fieldsOf } from './schema.js';
import type { SchemaField } from './schema.js';

/**
 * "Run it" — the difference between a claimed tool and a demonstrated one.
 *
 * Everything else on a capability card is the manifest describing itself. This
 * control calls the tool against the live origin through the same data plane an
 * agent would use and prints what came back, which is the only evidence on the
 * page that the tool works at all. It is also the fastest way to see the
 * binding-variable defect with your own eyes: pass two different enum members and
 * watch the same row come back twice.
 *
 * An error renders AS the error. An empty table where a failure belongs is how a
 * broken tool comes to look merely unpopular, and this dashboard exists to stop
 * exactly that reading.
 */

function Field(props: {
  cap: Capability;
  field: SchemaField;
  value: string;
  onInput: (value: string) => void;
}): VNode {
  const { cap, field: f } = props;
  const id = `run-${cap.name}-${f.name}`;
  const label = html`<label class="runf__label" for=${id}>
    <code>${f.name}</code>
    ${f.required
      ? html`<span class="req" aria-label="required">required</span>`
      : html`<span class="opt">optional</span>`}
    <span class="runf__type">${f.type}</span>
  </label>`;

  if (f.enumValues) {
    return html`
      <div class="runf">
        ${label}
        <select
          id=${id}
          class="runf__input"
          required=${f.required}
          value=${props.value}
          onChange=${(e: Event) => props.onInput((e.target as HTMLSelectElement).value)}
        >
          <option value="">${f.required ? 'Choose one…' : '(not passed)'}</option>
          ${f.enumValues.map(
            (v) => html`<option value=${v} selected=${props.value === v}>${v}</option>`,
          )}
        </select>
      </div>
    `;
  }

  const numeric = f.type === 'integer' || f.type === 'number';
  return html`
    <div class="runf">
      ${label}
      <input
        id=${id}
        class="runf__input"
        type=${numeric ? 'number' : 'text'}
        inputmode=${f.type === 'integer' ? 'numeric' : undefined}
        step=${f.type === 'integer' ? '1' : undefined}
        min=${f.minimum !== null ? String(f.minimum) : undefined}
        max=${f.maximum !== null ? String(f.maximum) : undefined}
        required=${f.required}
        autocomplete="off"
        spellcheck="false"
        placeholder=${f.fallback !== undefined ? `default: ${String(f.fallback)}` : ''}
        value=${props.value}
        onInput=${(e: Event) => props.onInput((e.target as HTMLInputElement).value)}
      />
    </div>
  `;
}

function columnsOf(rows: Array<Record<string, unknown>>): string[] {
  const seen: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.includes(key)) seen.push(key);
    }
  }
  return seen;
}

function cell(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function Freshness(props: { result: RunResult }): VNode {
  const f = props.result.freshness;
  if (!f) {
    return html`<span class="fresh fresh--unknown">freshness not reported</span>`;
  }
  const tone = f.state === 'fresh' ? 'fresh--fresh' : f.state === 'stale' ? 'fresh--stale' : 'fresh--other';
  const age = f.ageSec !== null ? `${f.ageSec}s old` : null;
  return html`<span class=${`fresh ${tone}`}>
    ${f.state}${age ? html` · ${age}` : null}${f.ttlSec !== null ? html` · ttl ${f.ttlSec}s` : null}
  </span>`;
}

function Result(props: { result: RunResult }): VNode {
  const r = props.result;

  // An error is the answer, printed as one. The rows table is not rendered at
  // all in that case, so there is no empty grid to misread as "no results".
  if (r.error || !r.ok) {
    return html`
      <div class="runres runres--bad">
        <p class="runres__bar">
          <span class="runres__verdict runres__verdict--bad">Failed</span>
          <span class="runres__ms">${fmtMs(r.ms)}</span>
        </p>
        <p class="runres__err" role="alert">${r.error ?? 'the tool returned ok:false with no message'}</p>
        ${r.notes.length
          ? html`<ul class="runres__notes">${r.notes.map((n) => html`<li>${n}</li>`)}</ul>`
          : null}
      </div>
    `;
  }

  const cols = columnsOf(r.rows);
  return html`
    <div class="runres">
      <p class="runres__bar">
        <span class="runres__verdict">
          ${r.count} ${r.count === 1 ? 'row' : 'rows'}
        </span>
        ${Freshness({ result: r })}
        <span class="runres__ms">${fmtMs(r.ms)}</span>
      </p>
      ${r.notes.length
        ? html`<ul class="runres__notes">${r.notes.map((n) => html`<li>${n}</li>`)}</ul>`
        : null}
      ${r.rows.length
        ? Scroller({
            label: 'Returned rows',
            children: html`
              <table class="rowtable">
                <thead>
                  <tr>
                    ${cols.map((c) => html`<th scope="col">${c}</th>`)}
                  </tr>
                </thead>
                <tbody>
                  ${r.rows.map(
                    (row) => html`<tr>${cols.map((c) => html`<td>${cell(row[c])}</td>`)}</tr>`,
                  )}
                </tbody>
              </table>
            `,
          })
        : html`<p class="runres__empty">
            The call succeeded and returned zero rows. That is an${SP}
            <code>empty</code> outcome, not an error: the binding ran and the origin had nothing
            matching these arguments.
          </p>`}
    </div>
  `;
}

export function Runner(props: {
  cap: Capability;
  run: RunState;
  actions: DashboardActions;
}): VNode {
  const { cap, run, actions } = props;
  const fields = fieldsOf(cap.inputSchema);

  return html`
    <div class="run">
      <div class="run__head">
        <h4 class="run__h">Run it</h4>
        <p class="run__p">
          Calls <code>${cap.name}</code> against the live origin through the same data plane an
          agent uses, and prints what came back.
        </p>
      </div>

      <form
        class="run__form"
        onSubmit=${(e: Event) => {
          e.preventDefault();
          actions.runTool(cap.name);
        }}
      >
        ${fields.length
          ? html`<div class="run__fields">
              ${fields.map((f) =>
                Field({
                  cap,
                  field: f,
                  value: run.args[f.name] ?? '',
                  onInput: (value) => actions.setRunArg(cap.name, f.name, value),
                }),
              )}
            </div>`
          : html`<p class="run__none">No parameters — this tool takes no arguments.</p>`}

        <div class="run__actions">
          <button type="submit" class="btn" disabled=${run.running}>
            ${run.running ? 'Running…' : 'Run it'}
          </button>
          ${run.result || run.error
            ? html`<button
                type="button"
                class="btn btn--ghost"
                onClick=${() => actions.clearRun(cap.name)}
              >
                Clear result
              </button>`
            : null}
        </div>
      </form>

      ${ErrorNote({ message: run.error })}
      ${run.result ? Result({ result: run.result }) : null}
    </div>
  `;
}
